import { log } from '../utils/DebugLogger';
import { eventBus } from '../eventBus';
import { httpService } from '../services/httpService';

/**
 * Save and validation logic for ClienteForm.
 * Extracted from useClienteForm to keep file sizes manageable.
 *
 * O aceite LGPD NÃO é coletado por este formulário: o consentimento é do
 * titular e não entra no save do cliente.
 *
 * @param {Object} deps
 * @param {Function} deps.t - i18n translate function
 * @param {import('vue').Ref} deps.formData - reactive form data
 * @param {import('vue').Ref} deps.documentValidationError - document validation error ref
 * @param {import('vue').ComputedRef} deps.currentDocumentPresentation - current document label
 * @param {import('vue').Ref} deps.countryConfig - country configuration
 * @param {Object} deps.clienteApi - { createCliente, updateCliente }
 * @param {import('vue').ComputedRef} deps.prefill - prefill data ref
 * @param {Function} deps.emitFn - component emit function
 */
export function useClienteFormSave(deps: any) {
  const {
    t, formData,
    documentValidationError, currentDocumentPresentation,
    countryConfig, clienteApi, ocorrenciaApi, pendingObservation, prefill, emitFn
  } = deps;

  /**
   * Validate the client name (min 3 characters).
   */
  function validateNome() {
    const nome = formData.value.cliente_nome;
    return nome && nome.length >= 3;
  }

  /**
   * Full form validation. Returns { valid, missingFields }.
   */
  function validateForm() {
    const missingFields = [];

    log('ClienteForm', 'validateForm', 'Starting validation with formData:', {
      cliente_nome: formData.value.cliente_nome,
      cliente_franquia_id: formData.value.cliente_franquia_id,
      cliente_ddi: formData.value.cliente_ddi,
      cliente_ddd: formData.value.cliente_ddd,
      cliente_telefone: formData.value.cliente_telefone
    });

    if (!validateNome()) {
      missingFields.push(t('Nome do cliente') + ' (mínimo 3 caracteres)');
    }
    if (formData.value.cliente_franquia_id === '') {
      missingFields.push(t('Franquia'));
    }
    if (documentValidationError.value) {
      missingFields.push(t(currentDocumentPresentation.value.label) + ' - ' + documentValidationError.value);
    }
    if (formData.value.cliente_ddi === '') {
      missingFields.push(t('Telefone') + ' - DDI');
    }
    if (countryConfig.value.phoneAreaCodeRequired && formData.value.cliente_ddd === '') {
      missingFields.push(t('Telefone') + ' - DDD');
    }
    if (formData.value.cliente_telefone === '') {
      missingFields.push(t('Telefone') + ' - ' + t('Número'));
    }

    if (missingFields.length > 0) {
      log('ClienteForm', 'validateForm', 'Validation failed. Missing fields:', missingFields);
      eventBus.emit('cliente.save.invalid', { missingFields });
      return { valid: false, missingFields };
    }

    log('ClienteForm', 'validateForm', 'Validation passed successfully');
    return { valid: true, missingFields: [] };
  }

  /**
   * Create a new client.
   */
  async function createNewCliente() {
    const response = await clienteApi.createCliente(formData.value);
    const dados = response.dados || {};
    return dados.cliente_id || dados.id || dados.insert_id;
  }

  /**
   * Auto-link to WhatsApp chat if created from WhatsApp context.
   */
  async function autoLinkWhatsApp(newClienteId: any) {
    if (!newClienteId || !prefill.value?.phone) {
      return;
    }
    try {
      await httpService.post('/index.php/config/messaging_chat_clientes/link', {
        phone: prefill.value.phone.replace(/\D/g, ''),
        cliente_id: newClienteId,
        link_type: 'manual',
      }, {
        inputContract: 'MessagingChatClientes_LinkInput',
        responseContract: 'MessagingChatClientes_LinkResponse',
      });
      log('ClienteForm', 'save', 'Auto-linked to WhatsApp chat', {
        phone: prefill.value.phone, clienteId: newClienteId
      });
    } catch (linkError) {
      log('ClienteForm', 'save', 'Error auto-linking to WhatsApp chat', linkError);
    }
  }

  /**
   * Save pending observation (from inline ocorrencias rich-text on new-client form).
   * Only runs for new clients with non-empty content. Validation is enforced by
   * ocorrenciaApi.saveOcorrencia against the Ocorrencias_SaveInput contract.
   */
  async function savePendingObservation(newClienteId: number) {
    if (!newClienteId || formData.value.cliente_id !== 0) {
      return;
    }
    if (!ocorrenciaApi || typeof ocorrenciaApi.saveOcorrencia !== 'function') {
      return;
    }
    const content = pendingObservation?.value?.trim?.() ?? '';
    if (!content) {
      return;
    }
    try {
      await ocorrenciaApi.saveOcorrencia({
        owner: 'cliente',
        ownerID: Number(newClienteId),
        content
      });
      if (pendingObservation) {
        pendingObservation.value = '';
      }
    } catch (obsError) {
      log('ClienteForm', 'savePendingObservation', 'Error saving inline observation', obsError);
      // O cliente já existe; só a observação ficou de fora. Avisar, em vez de
      // sumir com o texto em silêncio — o balcão não tem como saber de outro jeito.
      eventBus.emit('globalMessage.warning', t('Cliente salvo, mas a observação não foi registrada. Adicione-a de novo na ficha do cliente.'));
    }
  }

  /**
   * Emit the cliente.saved eventBus event with summary data.
   */
  function emitClienteSaved(newClienteId: number | undefined | null) {
    eventBus.emit('cliente.saved', {
      cliente_id: newClienteId != null ? newClienteId : (formData.value.cliente_id || undefined),
      cliente_nome: formData.value.cliente_nome,
      cliente_franquia_id: formData.value.cliente_franquia_id,
      cliente_ddi: formData.value.cliente_ddi,
      cliente_ddd: formData.value.cliente_ddd,
      cliente_telefone: formData.value.cliente_telefone,
      cliente_cpf: formData.value.cliente_cpf,
      cliente_email: formData.value.cliente_email
    });
  }

  /**
   * Save (create or update) the client. Handles LGPD + WhatsApp auto-link.
   */
  async function saveCliente() {
    log('ClienteForm', 'save', 'Saving client');

    const validation = validateForm();
    if (!validation.valid) {
      const msg = t('Campos obrigatórios faltando:') + '\n• ' + validation.missingFields.join('\n• ');
      eventBus.emit('globalMessage.error', msg);
      return;
    }

    try {
      const newClienteId = formData.value.cliente_id === 0
        ? await createNewCliente()
        : await updateExisting();

      await autoLinkWhatsApp(newClienteId);
      await savePendingObservation(newClienteId);

      eventBus.emit('globalMessage.success', t('Dados salvos com sucesso'));
      emitFn('save-success');
      emitClienteSaved(newClienteId);

      // O modal de link de pré-cadastro NÃO abre mais automaticamente após
      // salvar (decisão de produto, 2026-08-04): interrompia o fluxo do balcão
      // a cada cliente novo com cadastro incompleto. O envio continua
      // disponível pelo botão "Enviar link de pré-cadastro" no ClienteModal.
    } catch (error) {
      log('ClienteForm', 'save', 'Error saving client', error);
      if (error?.existingCliente) {
        eventBus.emit('globalMessage.action', {
          message: t('Este documento já pertence a: ') + error.existingCliente.cliente_nome,
          actionLabel: t('Abrir cadastro'),
          event: 'modal.cliente.open',
          payload: { id: error.existingCliente.cliente_id },
        });
      } else {
        eventBus.emit('globalMessage.error', t('Erro ao salvar cliente: ') + error.message);
      }
      eventBus.emit('cliente.save.failed', {
        reason: error?.message ?? String(error),
        cliente_id: formData.value.cliente_id,
      });
    }
  }

  /**
   * Update existing client. Returns null (no new ID).
   */
  async function updateExisting() {
    await clienteApi.updateCliente(formData.value);
    return null;
  }

  return { validateNome, validateForm, saveCliente };
}
